import { type Actor, actorSatisfies } from './actor';
import { type Action, allow, type Decision, deny } from './types';

/**
 * ONE decision, staged, first denial wins.
 *
 *   decide(actor, facts, action)
 *       │
 *       ├─ ①ᵇ VISIBILITY CASCADE  non-view actions first pass view —
 *       │                         you cannot act on what you cannot see
 *       ├─ ①  ACTOR GATE          requires[action] ⊆ actor.permissions.
 *       │                         Per-action lists, ALL EMPTY today. actor.ts.
 *       ├─ ②  ENTITY RULES        status · lifecycle · related-entity facts
 *       └─ ③  FIELD RULES         per-field override on view/edit with a field
 *              ▼
 *          Decision
 */
/**
 * EMPTY or COMPLETE on purpose. Production policies use the literal empty map
 * today. Once any Clerk restriction is declared, every action key and named op
 * must carry a list, so a partially configured permission gate cannot fail open.
 */
type EmptyRequires = { view?: never; add?: never; edit?: never; delete?: never; perform?: never };

type RestrictedRequires<Op extends string> = {
  view: readonly string[];
  add: readonly string[];
  edit: readonly string[];
  delete: readonly string[];
  perform: Record<Op, readonly string[]>;
};

export type Requires<Op extends string = never> = EmptyRequires | RestrictedRequires<Op>;

export type PolicySpec<Facts, Field extends string, Op extends string = never> = {
  /** Lower-case noun phrase used in generated messages, e.g. 'sales order line'. */
  kind: string;
  /** Every field this policy can be asked about. Grid column keys. May be empty. */
  fields: readonly Field[];
  /** Every named operation this policy can be asked about ('void', …). May be empty. */
  ops: readonly Op[];
  /**
   * Permissions the actor must hold, PER ACTION. EMPTY IN EVERY POLICY TODAY —
   * this is the Clerk seam. Filling it in is the only change required when
   * permissions ship. `view` ≠ `edit` ≠ `perform.void` are separate grants.
   */
  requires: Requires<Op>;
  /**
   * Entity-wide rules. Runs for every action type. MUST return allow() for
   * `view` — state locks gate change, never sight; visibility is a permission
   * concern. conformance.test.ts enforces this.
   */
  entity: (facts: Facts, action: Action<Field, Op>) => Decision;
  /** Per-field overrides, consulted when view/edit names a field and the entity allows. */
  field?: Partial<Record<Field, (facts: Facts, action: Action<Field, Op>) => Decision>>;
};

export type Policy<Facts, Field extends string, Op extends string = never> = {
  kind: string;
  fields: readonly Field[];
  ops: readonly Op[];
  requires: Requires<Op>;
  decide: (actor: Actor, facts: Facts, action: Action<Field, Op>) => Decision;
};

export type InferPolicyTypes<P> = P extends Policy<infer Facts, infer Field, infer Op>
  ? { facts: Facts; field: Field; op: Op; action: Action<Field, Op> }
  : never;

const requiredFor = <Op extends string>(requires: Requires<Op>, action: Action<string, Op>): readonly string[] => {
  if (requires.view === undefined) return [];
  if (action.type === 'perform') return requires.perform[action.op];
  return requires[action.type];
};

const verbFor = (action: Action<string, string>): string => {
  if (action.type === 'perform') return action.op;
  if (action.type === 'add') return 'add to';
  return action.type;
};

export const definePolicy = <Facts, Field extends string, Op extends string = never>(
  spec: PolicySpec<Facts, Field, Op>
): Policy<Facts, Field, Op> => {
  const evaluate = (actor: Actor, facts: Facts, action: Action<Field, Op>): Decision => {
    if (!actorSatisfies(actor, requiredFor(spec.requires, action))) {
      return deny('NOT_PERMITTED', `You do not have permission to ${verbFor(action)} this ${spec.kind}.`);
    }

    const entityDecision = spec.entity(facts, action);
    if (!entityDecision.allowed) return entityDecision;

    if ((action.type === 'view' || action.type === 'edit') && action.field !== undefined) {
      const fieldRule = spec.field?.[action.field];
      if (fieldRule) return fieldRule(facts, action);
    }

    return allow();
  };

  return {
    kind: spec.kind,
    fields: spec.fields,
    ops: spec.ops,
    requires: spec.requires,
    decide: (actor, facts, action) => {
      if (action.type !== 'view') {
        const visible = evaluate(actor, facts, { type: 'view', field: 'field' in action ? action.field : undefined });
        if (!visible.allowed) return visible;
      }

      return evaluate(actor, facts, action);
    },
  };
};
